/**
 * Which kind of problem a login / connection error is (shared by web and desktop; the mobile app uses the same classification in Dart).
 * Only classifies here, no text: the UI gives one plain sentence + what to do per kind (apps/web/src/lib/friendly-error.ts), with the raw message under "详情" (Details).
 * Errors from the desktop main process arrive over IPC as just one line of text, so tagged() prepends the kind: `[[tls]] raw message`.
 */

export type ErrorKind =
  /** This device is offline */
  | 'offline'
  /** Domain can't be resolved */
  | 'dns'
  /** Can't connect (refused, reset, host unreachable) */
  | 'unreachable'
  /** Timeout */
  | 'timeout'
  /** HTTPS / certificate / handshake */
  | 'tls'
  /** Malformed URL (or http:// used for remote) */
  | 'address'
  /** The address isn't a Momomail server */
  | 'not-momomail'
  /** A local-mode backend on someone else's computer, which doesn't accept logins */
  | 'local-mode'
  /** Wrong username or password */
  | 'credentials'
  /** Too many wrong passwords, rate-limited */
  | 'rate-limited'
  /** Account disabled */
  | 'disabled'
  /** Server 5xx */
  | 'server'
  /** App and server API versions don't match */
  | 'version'
  | 'unknown';

const KINDS: readonly ErrorKind[] = ['offline', 'dns', 'unreachable', 'timeout', 'tls', 'address', 'not-momomail', 'local-mode', 'credentials', 'rate-limited', 'disabled', 'server', 'version', 'unknown'];

/** An error with a kind: message is the raw message (for "详情" (Details)), kind decides what the UI says. */
export class ClassifiedError extends Error {
  constructor(
    readonly kind: ErrorKind,
    message: string,
  ) {
    super(message);
    this.name = 'ClassifiedError';
  }
}

/** When passing through places that only carry strings (Electron IPC), write the kind into the text. */
export function tagged(kind: ErrorKind, detail: string): string {
  return `[[${kind}]] ${detail}`;
}

/** HTTP status code → kind (for login / public no-login endpoints; a 401 after login means "session expired" and doesn't go through here). */
export function kindFromStatus(status: number): ErrorKind {
  if (status === 401) return 'credentials';
  if (status === 429) return 'rate-limited';
  if (status === 404) return 'not-momomail';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** Errors thrown by fetch in Node / the Electron main process (undici: TypeError('fetch failed'), reason in cause.code) → kind. */
export function kindFromNodeError(error: unknown): ErrorKind {
  const e = error as { name?: string; code?: string; message?: string; cause?: { code?: string; name?: string; message?: string } } | null;
  if (!e) return 'unknown';
  const name = e.cause?.name ?? e.name ?? '';
  if (name === 'TimeoutError' || name === 'AbortError' || name === 'ConnectTimeoutError') return 'timeout';
  const code = String(e.cause?.code ?? e.code ?? '');
  const text = `${code} ${e.cause?.message ?? ''} ${e.message ?? ''}`;
  if (/^(ENOTFOUND|EAI_AGAIN|EAI_NONAME|EAI_FAIL)$/.test(code)) return 'dns';
  if (/^(ENETDOWN)$/.test(code)) return 'offline';
  if (/^(ETIMEDOUT|ESOCKETTIMEDOUT|UND_ERR_CONNECT_TIMEOUT|UND_ERR_HEADERS_TIMEOUT|UND_ERR_BODY_TIMEOUT)$/.test(code)) return 'timeout';
  if (/CERT|UNABLE_TO_(GET|VERIFY)|SELF_SIGNED|DEPTH_ZERO|ERR_TLS|ERR_SSL|EPROTO|HANDSHAKE|ALTNAME/i.test(text)) return 'tls';
  if (/^(ECONNREFUSED|ECONNRESET|EHOSTUNREACH|ENETUNREACH|EPIPE|ECONNABORTED|UND_ERR_SOCKET|UND_ERR_CLOSED)$/.test(code)) return 'unreachable';
  return 'unknown';
}

/** Raw message of a Node error (shown under "详情" (Details) for those who understand it): fetch failed: ECONNREFUSED connect ECONNREFUSED 1.2.3.4:443 */
export function nodeErrorDetail(error: unknown): string {
  const e = error as { message?: string; cause?: { code?: string; message?: string } } | null;
  if (!e) return String(error);
  const cause = e.cause ? `${e.cause.code ? `${e.cause.code} ` : ''}${e.cause.message ?? ''}`.trim() : '';
  return [e.message ?? String(error), cause].filter(Boolean).join(': ');
}

/**
 * Any error → kind + raw message. Recognizes: ClassifiedError, text tagged with [[kind]] (desktop main process),
 * the backend's few fixed messages, and browser fetch network errors (TypeError, no details available: if online, treat as "can't connect").
 */
export function classifyError(error: unknown, online = true): { kind: ErrorKind; detail: string } {
  if (error instanceof ClassifiedError) return { kind: error.kind, detail: error.message };
  let text = error instanceof Error ? error.message : String(error);
  // Electron wraps main-process errors like this
  text = text.replace(/^Error invoking remote method '[^']+': (Error: )?/, '').replace(/^Error: /, '');
  const m = /^\[\[([a-z-]+)\]\]\s*([\s\S]*)$/.exec(text);
  if (m && KINDS.includes(m[1] as ErrorKind)) return { kind: m[1] as ErrorKind, detail: m[2]! };
  if (/用户名或密码不对|Incorrect username or password/i.test(text)) return { kind: 'credentials', detail: text };
  if (/次数太多|Too many (failed|wrong)/i.test(text)) return { kind: 'rate-limited', detail: text };
  if (/已停用|is disabled/i.test(text)) return { kind: 'disabled', detail: text };
  if (error instanceof TypeError && /fetch|network|load failed/i.test(text)) return { kind: online ? 'unreachable' : 'offline', detail: text };
  return { kind: 'unknown', detail: text };
}
